import React from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: '#f8fafc',
  color: '#333333',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
}));

// Styled breadcrumb link
const BreadcrumbLink = styled(RouterLink)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
  display: 'inline-flex',
  alignItems: 'center',
}));

// Styled breadcrumb item
const BreadcrumbItem = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  fontSize: '1.1rem',
  display: 'inline-flex',
  alignItems: 'center',
  '&.MuiTypography-root': {
    marginBottom: 0,
  }
}));

// Styled breadcrumb separator
const BreadcrumbSeparator = styled(ChevronRightIcon)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: '1.2rem',
  margin: '0 0.5rem',
  display: 'inline-flex',
  alignItems: 'center',
}));

const AppHeader = ({ 
  open, 
  handleDrawerOpen, 
  title,
  customHeader,
  breadcrumbs,
  actions,
  user
}) => {
  // If customHeader is provided, render it instead of the default header
  if (customHeader) {
    return (
      <AppBar position="fixed" open={open} elevation={0}>
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          minHeight: '56px',
          p: 1
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 2,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {customHeader}
        </Toolbar>
      </AppBar>
    );
  }

  // Default header with title and optional breadcrumbs
  return (
    <AppBar position="fixed" open={open} elevation={0}>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        minHeight: '56px',
        p: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 1,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Combine title and breadcrumbs in a horizontal layout */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '40px' 
              }}>
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  const isFirst = index === 0;
                  
                  return (
                    <React.Fragment key={index}>
                      {!isFirst && <BreadcrumbSeparator />}
                      
                      {isLast ? (
                        <BreadcrumbItem noWrap>
                          {crumb.label}
                        </BreadcrumbItem>
                      ) : (
                        <BreadcrumbLink to={crumb.href}>
                          {crumb.label}
                        </BreadcrumbLink>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            ) : (
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              >
                {title || 'Dashboard'}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* If custom actions are provided, render them */}
          {actions}

          {/* Default user area */}
          <Badge badgeContent={4} color="primary">
            <NotificationsIcon />
          </Badge>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {user ? user.substring(0, 1).toUpperCase() : 'U'}
          </Avatar>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader; 